import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import { Box, IconButton, Typography, Tooltip, Button, Stack, TooltipProps } from "@mui/material";
import { GridColDef, GridPaginationModel, GridRenderCellParams, GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import React, { useEffect, useState, useRef, useMemo, ReactNode, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useConfig } from "../../contexts/ConfigContext";
import { useOptions } from "../../contexts/OptionsContext";
import { ApiEndpoint, MediaItemDetailed, MediaResponse, SpatialDatasetType } from "../../types";
import { apiFetch, REACT_APP_API_ROOT } from "../../utilities/api";
import { QuickNewItemDialog } from "../common/QuickNewItemDialog";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import { useSpinner } from "../../contexts/SpinnerContext";
import MediaViewer from "../media/MediaViewer";
import { handleFormSubmissionError } from "../../utilities/errorHandlers";
import CustomDataGrid from "./data-table/CustomDataGrid";
import CustomPagination from "./data-table/CustomPagination";
import { getDataItemDescription, getDataItemName } from "../../utilities/dataSourceAdapters";
import { generateRelationshipColumn } from "../../constants/dataGrid";
import { DATASET_TYPE_LABEL, SOURCE_FORMAT_DATASET_TYPE, SOURCE_FORMAT_LABEL } from "../../constants/spatialDataset";
import { getEntityCategory } from "../../utilities/common";
import { findEndpointByKey, relatedItemsFor, withLinksTo } from "../../utilities/relationshipUtils";
import { getMediaType } from "../../utilities/mediaUtils";
import DeleteDialog from "../alerts-dialog/DeleteDialog";
import { RemoveCircleOutline } from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";

export interface EditorItemTableProps {
  items: MediaItemDetailed[];
  selectedItemId?: string | number;
  showFooterAction?: boolean;
  tableType?: string;
  mode?: "upload" | "edit";
  hasFilterApplied?: boolean;
  isLoading?: boolean;
  page?: number;
  pageSize?: number;
  isActionPending?: boolean;
  totalRowCounts?: number;
  searchTerm?: string;
  currentEndpoint: ApiEndpoint | undefined;
  footerActionContent?: ReactNode;
  paginationMode?: "client" | "server";
  customColumns?: GridColDef[] | ((helpers: { onCreateNewEntity: (onSuccess: (newItemId: string) => void) => void }) => GridColDef[]);
  hideFooter?: boolean;
  onItemSelect?: (item: MediaItemDetailed | undefined) => void;
  onPreviewClick?: (item: { mediaUrl: string; mediaType?: string; thumbnail?: string }) => void;
  onItemUpdate?: (item: Partial<MediaItemDetailed>, old?: MediaItemDetailed) => void;
  onBulkUpdate?: (updates: Array<{ id: string; updates: Partial<MediaItemDetailed> }>) => void;
  onFilesDropped?: (files: File[]) => void;
  onDeleteItem?: (id: string) => void;
  onPaginationChange?: (pagination: GridPaginationModel) => void;
  onSave?: () => void;
  onCancel?: () => void;
  hasChanges?: boolean;
  /** Called when a relationship cell selection is confirmed. Receives the selected IDs and the row ID. */
  onRelationshipSaved?: (tableSlug: string, ids: number[], rowId: string | number) => void;
  /**
   * Called when the "Restrictions" cell selection is confirmed. Receives the
   * selected restriction IDs and the row ID. Restrictions are not a
   * relationship — they're PATCHed directly onto the row/media item.
   */
  onRestrictionsSaved?: (ids: number[], rowId: string | number) => void;
}

export function CustomDataGridTooltip({ title, componentsProps, ...rest }: TooltipProps) {
  return (
    <Tooltip
      title={title}
      arrow
      placement='top'
      componentsProps={{
        ...componentsProps,
        tooltip: {
          ...componentsProps?.tooltip,
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            borderRadius: "0.5rem",
            border: "1px solid",
            borderColor: "grey.200",
            boxShadow: "0 12px 16px -4px #10182814, 0 4px 6px -2px #10182808",
            padding: "0.5rem 0.75rem",
            ...componentsProps?.tooltip?.sx
          }
        }
      }}
      {...rest}
    />
  );
}

// Custom real-time edit cell that updates the UI immediately while typing
function RealTimeEditInput(props: GridRenderEditCellParams) {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Update internal DataGrid edit state instantly
    apiRef.current.setEditCellValue({ id, field, value: newValue }, e);

    // Dispatch custom event to sync with SingleItemPreview instantly
    // bypassing row state updates that cause DataGrid to lose focus
    const event = new CustomEvent("live-preview-update", {
      detail: { id: String(id), field, value: newValue }
    });
    window.dispatchEvent(event);
  };

  const isAtLimit = (field === "name" && (value?.length || 0) >= 100) || (field === "description" && (value?.length || 0) >= 250);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        px: "0.5rem",
        border: isAtLimit ? "1px solid" : "none",
        borderColor: isAtLimit ? "error.main" : "transparent"
      }}
    >
      <input
        autoFocus
        value={value || ""}
        onChange={handleChange}
        maxLength={field === "name" ? 100 : field === "description" ? 250 : undefined}
        style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontSize: "0.875rem", fontFamily: "inherit" }}
      />
    </Box>
  );
}

export function EditorItemTable({
  items,
  onItemSelect,
  selectedItemId,
  onBulkUpdate,
  onItemUpdate,
  onFilesDropped,
  onDeleteItem,
  tableType = "media",
  mode = "upload",
  isLoading: isPending,
  onPaginationChange,
  page = 0,
  pageSize = 100,
  onSave,
  onCancel,
  showFooterAction = true,
  isActionPending,
  totalRowCounts = 0,
  searchTerm = "",
  currentEndpoint,
  footerActionContent,
  customColumns,
  paginationMode = "client",
  hideFooter: hidePagination = false,
  hasChanges = false,
  onRelationshipSaved,
  onRestrictionsSaved
}: EditorItemTableProps) {
  const config = useConfig();
  const { options, refreshOptions } = useOptions();
  const { canManageRestrictions } = useAuth();
  const [newItemDialog, setNewItemDialog] = useState<{
    open: boolean;
    category: string;
    endpoint: string;
    requireDescription?: boolean;
    onSuccess: (createdItem?: { id: number; name: string }) => void;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [disabledDeleteButtons, setDisabledDeleteButtons] = useState<Set<string>>(new Set());
  const { startLoading, stopLoading } = useSpinner();
  const [previewItem, setPreviewItem] = useState<MediaItemDetailed | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [blobUrls, setBlobUrls] = useState<Map<string, string>>(new Map());

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: page - 1, pageSize });

  const [itemToDelete, setItemToDelete] = useState<MediaItemDetailed | undefined>(undefined);
  // const [showDeleteConfirmationAlert, setShowDeleteConfirmationAlert] = useState(false);

  const isFulcrumEndpoint = currentEndpoint?.dataSourceType === "fulcrum";

  useEffect(() => {
    setPaginationModel({ page: page - 1, pageSize });
  }, [page, pageSize]);

  // Local filtering of data based on searchTerm using generalized getter functions
  const filteredData = useMemo(() => {
    let processedData = items;

    // Second filter: search term filtering
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      processedData = processedData.filter((item: any) => {
        const itemName = getDataItemName(item, currentEndpoint || undefined);
        const itemDescription = getDataItemDescription(item, currentEndpoint || undefined);
        return itemName?.toLowerCase().includes(lowerSearchTerm) || itemDescription?.toLowerCase().includes(lowerSearchTerm);
      });
    }

    // Return both the filtered data and counts as an object
    return {
      data: processedData,
      filteredCount: processedData.length
    };
  }, [items, searchTerm, currentEndpoint]);

  // Add drop-zone functionality
  const { getRootProps, isDragActive } = useDropzone({
    onDrop: onFilesDropped
  });

  // Validate config has required fields
  const validateConfig = () => {
    if (!config) {
      throw new Error("Configuration is not loaded");
    }
    return true;
  };

  const optionsRef = useRef(options);
  const itemsRef = useRef(items);

  useEffect(() => {
    optionsRef.current = options;
    itemsRef.current = items;
  }, [options, items]);

  // Store props in refs to avoid recreating `handleAddNew` and `columns`
  // on every selection change or parent render, which abruptly stops DataGrid editing.
  const selectedItemIdRef = useRef(selectedItemId);
  const onItemUpdateRef = useRef(onItemUpdate);
  // Always points at the latest `handleFillDown`. The column header is memoized on
  // `[selectedItemId]`, so without this ref it would keep a stale closure that reads
  // the pre-edit `items` (making fill-down copy empty relationships to rows below).
  const handleFillDownRef = useRef<(field: string) => void>(() => {});

  useEffect(() => {
    selectedItemIdRef.current = selectedItemId;
    onItemUpdateRef.current = onItemUpdate;
  }, [selectedItemId, onItemUpdate]);

  const handleAddNew = useCallback(
    (category: string, onSuccess: (newItemId: number) => void) => {
      const endpointConfig = config.apiEndpoints.find((e) => e.name.toLowerCase() === category?.toLowerCase());

      if (!endpointConfig) {
        enqueueUniqueSnackbar(enqueueSnackbar, `Configuration error for ${category}`, { variant: "error" });
        return;
      }

      // block fulcrum item creation
      if (endpointConfig.dataSourceType === "fulcrum") {
        enqueueUniqueSnackbar(enqueueSnackbar, `Cannot create new ${category} items: this data comes from an external data source.`, {
          variant: "error"
        });
        return;
      }

      if (!selectedItemIdRef.current) {
        enqueueUniqueSnackbar(enqueueSnackbar, "No item selected to update", { variant: "error" });
        return;
      }

      setNewItemDialog({
        open: true,
        category,
        endpoint: endpointConfig.api_endpoint,
        requireDescription: endpointConfig.tableName === "restrictions",
        onSuccess: async (createdItem?: { id: number; name: string }) => {
          try {
            if (!createdItem?.id || !createdItem?.name) {
              throw new Error("Invalid created item data");
            }
            startLoading();

            await refreshOptions();
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Hand the new item to the picker (MultiSelectDialog) that asked for it. It
            // updates the cell being edited, and processRowUpdate saves it with the row;
            // updating the row here would abort the grid's edit mode.
            onSuccess(createdItem.id);

            setNewItemDialog(null);
            enqueueSnackbar("Item created and added successfully", { variant: "success" });
          } catch (error) {
            enqueueUniqueSnackbar(
              enqueueSnackbar,
              error instanceof Error ? error.message : "Error creating or adding item. Please try again.",
              { variant: "error" }
            );
          } finally {
            stopLoading();
          }
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, enqueueSnackbar, refreshOptions, startLoading, stopLoading]
  );

  const handleCreateNewItem = async (name: string, description: string) => {
    setError(null);
    if (!newItemDialog) return;

    try {
      if (!name.trim()) {
        enqueueSnackbar("Name is required", { variant: "error" });
        return;
      }
      if (name.length > 100) {
        enqueueSnackbar("Name must be 100 characters or less", { variant: "error" });
        return;
      }
      if (description.length > 250) {
        enqueueSnackbar("Description must be 250 characters or less", { variant: "error" });
        return;
      }
      validateConfig();

      const endpointConfig = config.apiEndpoints.find((e) => e.name.toLowerCase() === newItemDialog.category.toLowerCase());

      if (!endpointConfig || !endpointConfig.api_endpoint) {
        throw new Error(`No endpoint configured for ${newItemDialog.category}`);
      }

      const payload = {
        name: name.trim(),
        description: description.trim(),
        primary_image_write: null
      };

      const response = await apiFetch(`${REACT_APP_API_ROOT}${endpointConfig.api_endpoint}`, {
        method: "POST",
        body: JSON.stringify(payload)
      });

      newItemDialog.onSuccess({ ...(response?.data || response) });
      setNewItemDialog(null);
    } catch (error) {
      handleFormSubmissionError(error, enqueueSnackbar, {
        action: "create",
        itemType: "item"
      });
      throw error;
    }
  };

  // Add safety check for selectedItemId
  useEffect(() => {
    if (selectedItemId && !items.some((item) => item.id === selectedItemId)) {
      // If selected item no longer exists, clear selection
      onItemSelect?.(undefined);
    }
  }, [items, selectedItemId, onItemSelect]);

  // Cleanup blob URLs when component un-mounts or items change
  useEffect(() => {
    return () => {
      // Cleanup all blob URLs on unmount
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up blob URLs for items that no longer exist
  useEffect(() => {
    const currentItemIds = new Set(items.map((item) => String(item.id)));
    const urlsToCleanup: string[] = [];

    blobUrls.forEach((url, itemId) => {
      if (!currentItemIds.has(itemId)) {
        URL.revokeObjectURL(url);
        urlsToCleanup.push(itemId);
      }
    });

    if (urlsToCleanup.length > 0) {
      setBlobUrls((prev) => {
        const newMap = new Map(prev);
        urlsToCleanup.forEach((id) => newMap.delete(id));
        return newMap;
      });
    }
  }, [items, blobUrls]);

  const renderHeaderWithFillDown = useCallback(
    (label: string, field: string, showFillDown = true) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        {selectedItemId && showFillDown && (
          <Tooltip title='Apply this row’s value to all rows below'>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleFillDownRef.current?.(field);
              }}
              sx={{
                padding: 0.5,
                zIndex: 10,
                position: "relative",
                "&:hover": { backgroundColor: "primary.main" }
              }}
            >
              <KeyboardDoubleArrowDownIcon fontSize='medium' />
            </IconButton>
          </Tooltip>
        )}
        {label}
      </Box>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItemId]
  );

  const handleFillDown = useCallback(
    async (field: string) => {
      if (!selectedItemId) return;

      const selectedIndex = items.findIndex((item) => item.id === selectedItemId);
      if (selectedIndex === -1) {
        console.warn("Selected item no longer exists in table");
        onItemSelect?.(undefined);
        return;
      }

      const selectedItem = items[selectedIndex] as MediaItemDetailed;
      const rowsBelow = items.slice(selectedIndex + 1);
      if (rowsBelow.length === 0) return;

      // Handle basic fields (name and description)
      if (field === "name" || field === "description") {
        if (!onBulkUpdate) return;
        const updates = rowsBelow.map((item) => ({
          id: String(item.id),
          updates: {
            [field]: selectedItem[field]
          }
        }));
        onBulkUpdate(updates);
        return;
      }

      // Handle the Restrictions column (not a relationship — PATCHed directly on the row).
      if (field === "restrictions") {
        if (!onRestrictionsSaved) return;
        const restrictionIds = (((selectedItem as any)?.restrictions ?? []) as Array<{ id: number }>).map((r) => r.id);
        rowsBelow.forEach((item) => onRestrictionsSaved(restrictionIds, item.id));
        return;
      }

      // Handle relationship fields - look up endpoint by slug
      const endpoint = config.apiEndpoints.find((ep) => ep.tableName === field);
      if (!endpoint) return;

      // The selected row's links for this column, copied to every row below.
      const selectedLinks = selectedItem.relationships ? relatedItemsFor(selectedItem.relationships, endpoint) : [];
      const selectedIds = selectedLinks.map((item) => Number(item.id));

      // Preferred path: reuse the relationship-save callback so each row's draft is
      // seeded in the relationship-editor store. This is what EndpointEditor.onSave
      // reads to PUT the changes — without it, fill-down would show but never persist.
      if (onRelationshipSaved) {
        rowsBelow.forEach((item) => onRelationshipSaved(endpoint.tableName, selectedIds, item.id));
        return;
      }

      // Fallback (standalone EditorItemTable without the relationship store):
      // update the grid rows directly via onBulkUpdate.
      if (!onBulkUpdate) return;
      const updates = rowsBelow.map((item) => ({
        id: String(item.id),
        updates: { relationships: withLinksTo(item.relationships, endpoint, [...selectedLinks]) }
      }));

      onBulkUpdate(updates);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItemId, onBulkUpdate, onRelationshipSaved, onRestrictionsSaved, items, onItemSelect, config.apiEndpoints]
  );

  // Keep the ref in sync so the memoized column header always invokes the latest
  // handleFillDown (which closes over the current `items`/relationships).
  useEffect(() => {
    handleFillDownRef.current = handleFillDown;
  }, [handleFillDown]);

  const handleProcessRowUpdate = (newRow: any, oldRow: MediaItemDetailed) => {
    if (isFulcrumEndpoint) return;

    // Process updates to table rows
    try {
      if (!items.some((item) => item.id === newRow.id)) {
        return oldRow;
      }

      let changedField: string | undefined = undefined;

      // Handle basic field updates
      if (newRow.name !== oldRow.name) {
        changedField = "name";
      } else if (newRow.description !== oldRow.description) {
        changedField = "description";
      }

      if (changedField) {
        const update: Partial<MediaItemDetailed> = {
          id: newRow.id,
          [changedField]: newRow[changedField]
        };
        if (onItemUpdate) {
          onItemUpdate(update, oldRow);
        }
        return { ...oldRow, ...update };
      }

      // If basic fields haven't changed, fall back to relationship logic
      changedField = Object.keys(newRow).find((key) => JSON.stringify(newRow[key]) !== JSON.stringify(oldRow[key]));

      if (!changedField) return oldRow;

      // Find matching endpoint for relationship updates - prioritize slug
      const endpoint = findEndpointByKey(config.apiEndpoints, changedField);

      if (!endpoint) return oldRow;

      // Format selected items for relationship update
      const selectedItems = newRow[changedField].map((id: number) => {
        const optionItem = options?.[endpoint.tableName]?.find((opt) => opt.id === id);
        return {
          id,
          entity_name: optionItem?.name,
          rel_description: "Associated at Upload",
          is_primary: false
        };
      });

      // Create relationship update object
      const update: Partial<MediaItemDetailed> = {
        id: newRow.id,
        relationships: withLinksTo(oldRow.relationships, endpoint, selectedItems)
      };

      onItemUpdate?.(update);
      return { ...oldRow, ...update };
    } catch (error) {
      return oldRow;
    }
  };

  // First, separate the Preview column from other hardcoded columns
  const baseColumns: GridColDef[] = useMemo(
    () => [
      {
        field: "type",
        headerName: "Type",
        width: 90,
        sortable: false,
        renderCell(params: GridRenderCellParams<MediaItemDetailed>) {
          // Spatial upload rows carry their source_format (and mirror it into
          // media_type, which getMediaType can only ever read as "unknown").
          // Label them by the dataset_type the backend will derive from that
          // format — Raster for GeoTIFF, Vector for GeoJSON/KML/GPX/Shapefile,
          // Tabular for CSV — with the format itself in the tooltip.
          const sourceFormat = ((params?.row as any)?.source_format ?? params?.row?.media_type ?? "") as string;
          const datasetType = (SOURCE_FORMAT_DATASET_TYPE as Record<string, SpatialDatasetType | undefined>)[sourceFormat];
          if (datasetType) {
            return (
              <CustomDataGridTooltip title={(SOURCE_FORMAT_LABEL as Record<string, string>)[sourceFormat] ?? sourceFormat}>
                <Typography>{DATASET_TYPE_LABEL[datasetType]}</Typography>
              </CustomDataGridTooltip>
            );
          }

          const entityType = getEntityCategory(params?.row);
          const displayType =
            entityType === "media"
              ? // get specific type (image, audio, video, pdf.
                // If non of the type matches, show unknown) for media entity
                getMediaType(params?.row as unknown as MediaResponse)
              : // must be a geo/non-geo entity
                entityType;

          return <Typography sx={{ textTransform: "capitalize" }}>{displayType}</Typography>;
        }
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        maxWidth: 260,
        minWidth: 160,
        editable: true,
        sortable: false,
        renderEditCell: (params: GridRenderEditCellParams) => <RealTimeEditInput {...params} />,
        renderCell: (params: GridRenderCellParams<MediaItemDetailed>) => {
          const isFirstRow = params.api.getRowIndexRelativeToVisibleRows(params.id) === 0;
          const isEmpty = !params.value;

          if (isFirstRow && isEmpty) {
            return <span style={{ opacity: 0.6 }}>Start typing to add data…</span>;
          }
          const value = params.value?.toString() || "";

          // Create tooltip content with the same styling as relationship tooltips
          const tooltipContent = (
            <Box sx={{ p: 1, maxWidth: 300 }}>
              <Typography variant='body1'>{value}</Typography>
            </Box>
          );

          return (
            <CustomDataGridTooltip title={tooltipContent} arrow={false}>
              <Typography
                variant='caption'
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  fontSize: "0.75rem",
                  display: "block"
                }}
              >
                {value}
              </Typography>
            </CustomDataGridTooltip>
          );
        }
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1,
        maxWidth: 300,
        minWidth: 200,
        editable: true,
        sortable: false,
        renderEditCell: (params: GridRenderEditCellParams) => <RealTimeEditInput {...params} />,
        renderCell: (params: GridRenderCellParams<MediaItemDetailed>) => {
          const value = params.value?.toString() || "";

          // Create tooltip content with the same styling as relationship tooltips
          const tooltipContent = (
            <Box sx={{ p: 1, maxWidth: 300 }}>
              <Typography variant='body1'>{value}</Typography>
            </Box>
          );

          if (!value || !value?.trim()) {
            return (
              <Typography variant='caption' sx={{ color: "#98A2B3!important" }}>
                Click to add description...
              </Typography>
            );
          }
          return (
            <CustomDataGridTooltip title={tooltipContent}>
              <Typography
                variant='caption'
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  fontSize: "0.75rem",
                  display: "block"
                }}
              >
                {value}
              </Typography>
            </CustomDataGridTooltip>
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [renderHeaderWithFillDown]
  );

  // const handlePrepareDelete = async (id: string, item?: any) => {
  //   // Prevent multiple rapid clicks
  //   setItemToDelete(item);
  //
  //   // open delete confirmation alert
  //   if (onDeleteItem && mode === "edit") {
  //     setShowDeleteConfirmationAlert(true);
  //   } else {
  //     await handleDelete(item);
  //   }
  // };

  const handleDelete = async (value?: MediaItemDetailed) => {
    const item = value || itemToDelete;
    if (!item) return;
    const id = item?.id?.toString();

    try {
      // Clean up any managed blob URLs for the item being deleted
      const existingUrl = blobUrls.get(id);
      if (existingUrl) {
        URL.revokeObjectURL(existingUrl);
        setBlobUrls((prev) => {
          const newMap = new Map(prev);
          newMap.delete(id);
          return newMap;
        });
      }

      // If this is the selected row, clear selection first
      if (id?.toString() === selectedItemId?.toString()) {
        onItemSelect?.(undefined);
      }

      // Clear preview if showing deleted item
      if (previewItem?.id?.toString() === id?.toString()) {
        setPreviewItem(null);
        setIsPreviewOpen(false);
      }

      // Small delay to allow state updates to propagate
      await new Promise((resolve) => setTimeout(resolve, 50));

      // In edit mode, we might want to confirm deletion of server-side items
      if (mode === "edit") {
        const item = items.find((item) => item.id?.toString() === id?.toString());
        // Check if this is a server-side item (has a URL but no file)
        if (item?.url && !item.file) {
          // In a real implementation, we might show a confirmation dialog
          // setShowDeleteConfirmationAlert(true);
        }
      }

      // Call the delete handler
      onDeleteItem?.(id);
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Failed to delete item.", { variant: "error" });
    } finally {
      // Re-enable the button after a short delay
      setTimeout(() => {
        setDisabledDeleteButtons((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }, 100);
      // setShowDeleteConfirmationAlert(false);
      setItemToDelete(undefined);
    }
  };

  function handleCancelDelete() {
    const itemId = itemToDelete?.id;
    setDisabledDeleteButtons((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId?.toString()!);
      return newSet;
    });

    // setShowDeleteConfirmationAlert(false);
    setItemToDelete(undefined);
  }

  const deleteColumn: GridColDef = useMemo(
    () => ({
      field: "delete",
      headerName: "",
      width: 35,
      sortable: false,
      renderCell: (params) => (
        <CustomDataGridTooltip title='Remove the row'>
          <IconButton
            aria-label='Remove row'
            onClick={(e) => {
              e.stopPropagation();
              onDeleteItem?.(params?.row?.id);
              // handlePrepareDelete(params.row.id, params.row);
            }}
            disabled={disabledDeleteButtons.has(params.row.id?.toString())}
            color='error'
          >
            <RemoveCircleOutline fontSize='medium' sx={{ ":hover": { color: "error.main" } }} />
          </IconButton>
        </CustomDataGridTooltip>
      )
    }),
    [onDeleteItem, disabledDeleteButtons]
  );

  // Then combine them in the right order, ensuring unique field names
  const columns = useMemo(() => {
    const cols =
      typeof customColumns === "function"
        ? customColumns(handleAddNew as any)
        : customColumns || [
            ...baseColumns,
            ...config.apiEndpoints
              .filter((endpoint) => endpoint.tableName !== currentEndpoint?.tableName && endpoint.tableName !== tableType)
              .filter((endpoint) => endpoint.dataSourceType !== "fulcrum") // CM-FE160-32: never show Fulcrum as relationship columns
              .filter((endpoint) => endpoint.tableName !== "restrictions" || canManageRestrictions)
              .map((endpoint) => {
                return generateRelationshipColumn({
                  endpoint,
                  optionsRef,
                  onCreateNewRelationEntity: handleAddNew,
                  renderHeader: renderHeaderWithFillDown,
                  config,
                  currentEndpoint,
                  onSaved:
                    endpoint.tableName === "restrictions"
                      ? onRestrictionsSaved
                        ? (ids, rowId) => onRestrictionsSaved(ids, rowId!)
                        : undefined
                      : onRelationshipSaved
                        ? (ids, rowId) => onRelationshipSaved(endpoint.tableName, ids, rowId!)
                        : undefined
                });
                // return generateRelationshipColumn(endpoint, options, handleAddNew, renderHeaderWithFillDown);
              })
              .filter((column): column is GridColDef => column !== null),
            deleteColumn
          ];
    return cols;
  }, [
    customColumns,
    handleAddNew,
    baseColumns,
    config,
    currentEndpoint,
    tableType,
    deleteColumn,
    renderHeaderWithFillDown,
    onRelationshipSaved,
    onRestrictionsSaved,
    canManageRestrictions
  ]);

  const handleClosePreview = () => {
    // Only revoke URL if it's not one of our managed blob URLs
    if (previewUrl && !blobUrls.has(previewItem?.id ? String(previewItem.id) : "")) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewItem(null);
    setIsPreviewOpen(false);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const filesArray = Array.from(event.target.files);
      onFilesDropped?.(filesArray);

      // Clear the input so the same file can be selected again if needed
      event.target.value = "";
    }
  };

  const handleRowClick = useCallback(
    (params: any) => {
      // early abort is endpoint is Fulcrum, since we don't
      // want to show preview
      if (isFulcrumEndpoint) return;

      if (items.some((item) => item.id === params?.row.id)) {
        onItemSelect?.(params?.row);
      }
    },
    [onItemSelect, items?.length, isFulcrumEndpoint]
  );

  // Early returns must come AFTER all hooks are defined
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>{error}</Typography>
      </Box>
    );
  }

  if (items.length === 0 && mode === "upload") return null;

  return (
    <>
      <Box
        {...getRootProps()}
        sx={{
          height: "100%",
          width: "100%",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden" // Prevent double scrollbars
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hidden file input for "ADD MORE" button */}
        <input type='file' multiple ref={fileInputRef} style={{ display: "none" }} onChange={handleFileInputChange} />

        {/* Empty state overlay - only show when there are no items */}
        {items.length === 0 && mode === "upload" && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              cursor: "pointer",
              backgroundColor: "background.paper",
              "&:hover": {
                backgroundColor: "action.hover"
              }
            }}
          >
            <img src='icons/upload.png' alt='Upload' style={{ width: 48, height: 48 }} />
            <Typography variant='body1' color='text.secondary'>
              {isDragActive ? "Drop files here ..." : "Click to select files or drop them here"}
            </Typography>
          </Box>
        )}

        {/* DataGrid with updated styling */}
        <CustomDataGrid
          isCellEditable={() => !isFulcrumEndpoint}
          isRowSelectable={() => !isFulcrumEndpoint}
          disableRowSelectionOnClick={isFulcrumEndpoint}
          initialState={{
            columns: {
              columnVisibilityModel: {
                // show `type` column only for media endpoint or table
                type: tableType === "media"
              }
            },
            pagination: {
              paginationModel: { pageSize: pageSize, page }
            }
          }}
          rows={filteredData?.data}
          columns={columns}
          paginationMode={paginationMode}
          rowCount={totalRowCounts}
          onRowClick={handleRowClick}
          onCellEditStop={(params, event) => {
            if (params.reason === "cellFocusOut") {
              // Only prevent focus out for complex relationship fields (multiselect dialogs)
              // Basic fields like 'name' and 'description' should commit on blur
              if (params.field !== "name" && params.field !== "description") {
                event.defaultMuiPrevented = true;
              }
            }
          }}
          processRowUpdate={handleProcessRowUpdate}
          onProcessRowUpdateError={(error) => {
            console.error("Error updating row:", error);
            enqueueSnackbar("Failed to update row", { variant: "error" });
          }}
          // NOTE: Do NOT add a `getRowClassName` function here.
          // MUI's GridRow calls `getRowParams(rowId)` *before* its own
          // `if (!rowNode) return null` guard, but only when getRowClassName is a
          // function. During a server-side page change the virtualization render
          // context briefly references rows whose model has already been swapped
          // out, so getRowParams throws "No row with id #<id> found" and crashes
          // the editor (e.g. ?endpoint=media&page=2). The selected row is already
          // highlighted via MUI's built-in `.Mui-selected` class. Style selected/
          // error rows via `getCellClassName` or `.Mui-selected` if needed — both
          // run only after the stale-row guard.
          paginationModel={paginationModel}
          getRowId={(row) => {
            if (!row?.id) {
              console.warn("Row missing ID:", row);
              return Math.random().toString();
            }
            return row.id;
          }}
          loading={isPending}
          slots={{
            noResultsOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%"
                }}
              >
                <Typography color='text.secondary'>
                  {mode === "upload"
                    ? isDragActive
                      ? "Drop files here..."
                      : "No files selected"
                    : "No items to edit. Select items in the Tables tab first."}
                </Typography>
              </Box>
            ),
            pagination: CustomPagination
          }}
          onPaginationModelChange={(page) => {
            setPaginationModel({ ...page });
            if (onPaginationChange) {
              onPaginationChange(page);
            }
          }}
          hideFooter={hidePagination}
          footerContent={
            showFooterAction &&
            (footerActionContent || (
              <Stack direction='row' gap={24}>
                <Button variant='secondary-outlined' onClick={onCancel} disabled={isActionPending}>
                  Cancel
                </Button>
                <Button variant='contained' onClick={onSave} disabled={isActionPending || !hasChanges}>
                  Save
                </Button>
              </Stack>
            ))
          }
          // Add auto-height prop to ensure proper sizing
          autoHeight={false}
          sx={{ border: "0" }}
        />

        {/* Add a drop zone overlay when dragging - ALWAYS SHOW THIS WHEN DRAGGING REGARDLESS OF ITEM COUNT */}
        {isDragActive && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(25, 118, 210, 0.1)",
              border: "3px dashed #2196f3",
              borderRadius: "4px"
            }}
          >
            <Typography variant='h6' color='primary'>
              Drop files here to {mode === "upload" ? "upload" : "add to selection"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* QuickNewItemDialog */}
      {newItemDialog && (
        <QuickNewItemDialog
          open={newItemDialog.open}
          onClose={() => setNewItemDialog(null)}
          onSubmit={handleCreateNewItem}
          title={`Add New ${newItemDialog.category}`}
          category={newItemDialog.category}
          error={error}
          requireDescription={newItemDialog.requireDescription}
        />
      )}

      <MediaViewer item={previewItem} url={previewUrl || undefined} open={isPreviewOpen} onClose={handleClosePreview} mode='dialog' />

      {/* Permanent entity delete confirmation alert */}
      <DeleteDialog open={false} item={itemToDelete} onDelete={() => handleDelete()} onCancel={handleCancelDelete} />
    </>
  );
}
